import { Badge, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  CalendarRegular,
  WarningRegular,
  DocumentRegular,
  ClipboardTaskListLtrRegular,
  EditRegular,
  EyeRegular,
  ErrorCircleRegular,
  CloudArrowUpRegular,
  SignatureRegular,
  SendRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  MoneyRegular,
} from '@fluentui/react-icons';
import { TaxReturnStatus, statusDisplayNames } from '../types';
import { useStatusColorMaps } from '../theme/statusColors';

interface StatusBadgeProps {
  status: TaxReturnStatus;
  size?: 'small' | 'medium' | 'large';
  showIcon?: boolean;
  variant?: 'filled' | 'outline' | 'subtle';
  showTooltip?: boolean;
}

const useStyles = makeStyles({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  small: {
    fontSize: tokens.fontSizeBase100,
    padding: '2px 6px',
  },
  medium: {
    fontSize: tokens.fontSizeBase200,
    padding: '4px 10px',
  },
  large: {
    fontSize: tokens.fontSizeBase300,
    padding: '6px 14px',
  },
});

const statusIcons: Record<TaxReturnStatus, React.ReactNode> = {
  scheduled: <CalendarRegular />,
  pending_resolution: <WarningRegular />,
  documents_pending: <DocumentRegular />,
  ready_to_prepare: <ClipboardTaskListLtrRegular />,
  in_preparation: <EditRegular />,
  in_review: <EyeRegular />,
  needs_corrections: <ErrorCircleRegular />,
  ready_for_efile: <CloudArrowUpRegular />,
  ready_for_signature: <SignatureRegular />,
  waiting_for_payment: <MoneyRegular />,
  e_filed: <SendRegular />,
  completed: <CheckmarkCircleRegular />,
  inactive: <DismissCircleRegular />,
};

const statusDescriptions: Record<TaxReturnStatus, string> = {
  scheduled: 'Appointment is scheduled, awaiting client visit',
  pending_resolution: 'Needs attention - joint/separate filing decision required',
  documents_pending: 'Waiting for client to submit required documents',
  ready_to_prepare: 'Documents received, ready for a preparer to claim',
  in_preparation: 'Tax return is being prepared by staff',
  in_review: 'Return completed, under review by supervisor',
  needs_corrections: 'Return requires corrections before e-filing',
  ready_for_efile: 'Return is ready to be electronically filed',
  ready_for_signature: 'Return ready for client signature (8879)',
  waiting_for_payment: 'Signature complete; awaiting payment before e-file',
  e_filed: 'Return has been electronically filed with IRS',
  completed: 'All tasks complete, return finalized',
  inactive: 'Return marked as inactive - not filing',
};

export default function StatusBadge({
  status,
  size = 'medium',
  showIcon = true,
  variant = 'filled',
  showTooltip = false,
}: StatusBadgeProps) {
  const styles = useStyles();
  const { status: statusColors } = useStatusColorMaps();

  const getVariantStyles = () => {
    const baseColor = statusColors[status];
    switch (variant) {
      case 'filled':
        return {
          backgroundColor: baseColor,
          color: 'white',
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          border: `1px solid ${baseColor}`,
          color: baseColor,
        };
      case 'subtle':
        return {
          backgroundColor: `${baseColor}20`,
          color: baseColor,
        };
      default:
        return {
          backgroundColor: baseColor,
          color: 'white',
        };
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'small':
        return styles.small;
      case 'large':
        return styles.large;
      default:
        return styles.medium;
    }
  };

  const badge = (
    <Badge
      appearance="filled"
      className={mergeClasses(styles.badge, getSizeClass())}
      style={getVariantStyles()}
      icon={showIcon ? statusIcons[status] as React.ReactElement : undefined}
    >
      {statusDisplayNames[status]}
    </Badge>
  );

  if (showTooltip) {
    return (
      <Tooltip content={statusDescriptions[status]} relationship="description">
        {badge}
      </Tooltip>
    );
  }

  return badge;
}

export { statusIcons, statusDescriptions };
